import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { getSupportRequests, deleteSupportRequest, getExportUrl } from '@/lib/api';
import { formatDate, truncate } from '@/lib/utils';
import { toast } from 'sonner';
import { Trash2, Download, Mail, Phone, MapPin } from 'lucide-react';

const AdminSupportRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const fetchRequests = async () => {
    try {
      const response = await getSupportRequests();
      setRequests(response.data);
    } catch (error) {
      console.error('Error fetching requests:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const handleDelete = async () => {
    if (!itemToDelete) return;
    
    try {
      await deleteSupportRequest(itemToDelete.id);
      toast.success('Request deleted');
      fetchRequests();
    } catch (error) {
      toast.error('Failed to delete request');
    } finally {
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    }
  };

  const handleExport = () => {
    const token = localStorage.getItem('admin_token');
    const url = getExportUrl('support-requests');
    window.open(`${url}?token=${token}`, '_blank');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-brand-blue border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-support-requests-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-brand-blue">Support Requests</h1>
          <p className="text-gray-500 mt-1">Manage navigation support requests</p>
        </div>
        <Button 
          onClick={handleExport}
          className="bg-brand-teal hover:bg-brand-teal/90 text-white"
          data-testid="export-btn"
        >
          <Download size={18} className="mr-2" />
          Export CSV
        </Button>
      </div>

      {/* Table */}
      <Card className="bg-white rounded-xl">
        <CardContent className="p-0">
          {requests.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-gray-500">No support requests yet.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Support Type</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {requests.map((request) => (
                  <TableRow key={request.id} data-testid={`request-row-${request.id}`}>
                    <TableCell>
                      <p className="font-medium text-brand-blue">{request.full_name}</p>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <p className="text-sm flex items-center text-gray-600">
                          <Mail size={14} className="mr-1" />
                          {request.email}
                        </p>
                        <p className="text-sm flex items-center text-gray-600">
                          <Phone size={14} className="mr-1" />
                          {request.phone}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <p className="text-sm flex items-center text-gray-600">
                        <MapPin size={14} className="mr-1" />
                        {request.location}
                      </p>
                    </TableCell>
                    <TableCell>
                      <Badge className="bg-brand-teal/10 text-brand-teal">
                        {request.support_type}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <p className="text-gray-500 text-sm">{truncate(request.description, 60)}</p>
                    </TableCell>
                    <TableCell className="text-gray-500 text-sm">
                      {formatDate(request.created_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600 hover:bg-red-50"
                        onClick={() => {
                          setItemToDelete(request);
                          setDeleteDialogOpen(true);
                        }}
                        data-testid={`delete-${request.id}`}
                      >
                        <Trash2 size={16} />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Request</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this support request? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminSupportRequests;
